import {
  codeForLayoutToken,
  MOUSE_TOKEN_TO_ZONE,
  TOKEN_TO_CODE,
  tokenChar,
  tokenToCodes,
} from "@/domain/tokens";
import type {
  Binding,
  BoardLayer,
  CharBind,
  Keyboard,
  KeyboardLayout,
  Layer,
  Layers,
  Profile,
  QmkBoardLayer,
} from "@/domain/types";
import { isZone } from "@/domain/zones";
import { activeCodeOf, charMapOf, keycodedBoardLayer } from "@/hardware/keyboard-layouts/index";
import { modifierFamily } from "@/hardware/modifier-family";
import { normalizeCombo } from "./index";

/** Whether a token names a bare modifier (Ctrl/Control/Shift/Alt): projected onto the modifier
 * caps via the family, never through the layout's char map - so parsed char codes say nothing
 * about its mappability. */
export function isModifierToken(token: string): boolean {
  return token in MODIFIER_TOKEN;
}

/** The named modifier tokens a Profile may bind as a key. */
const MODIFIER_TOKEN: Record<string, "ctrl" | "alt" | "shift"> = {
  Ctrl: "ctrl",
  Control: "ctrl",
  Shift: "shift",
  Alt: "alt",
};

type ModMap = Record<"ctrl" | "alt" | "shift", string[]>;

// A modifier token resolves to the WHOLE family's standard positions on a universal Keyboard
// Layout: both physical keys fire the bind in game, and the board-layer path already paints every
// family member (matching by modifierFamily). Left-only was the original choice, deliberate but
// invisible while the KLE importer coded both physical caps *Left; once right-side modifiers
// carry their own codes, left-only silently un-paints them (maintainer decision 2026-07-02:
// family-wide everywhere). The modifier strip still latches by family, so nothing changes there.
// This constant only ever serves the universal path (a board layer resolves by active keycode).
const STANDARD_MODS: ModMap = {
  ctrl: ["ControlLeft", "ControlRight"],
  shift: ["ShiftLeft", "ShiftRight"],
  alt: ["AltLeft", "AltRight"],
};

/**
 * Resolve one character-space token to every bindable position it lands on through the lens: a zone
 * code is itself (mouse / wheel), a named modifier reads the modifier map, everything else goes
 * through tokenToCodes (chars, positional tokens, SC2 mouse tokens, F-keys, national OEM tokens).
 * Kept separate from tokenToCodes so SC2's parser keeps its closed token vocabulary (no wheel /
 * bare-modifier tokens), while hand-authored Profiles gain the wider one.
 */
function projectToken(token: string, charMap: KeyboardLayout, mods: ModMap): string[] {
  if (isZone(token) || ARROW.test(token)) return [token];
  const fam = MODIFIER_TOKEN[token];
  if (fam) return mods[fam];
  return tokenToCodes(token, charMap);
}

// Arrow keys are positional inputs that emit no character (Destiny binds emotes to them); they pass
// through as their own code. Outside SC2's closed token vocabulary, so they only reach hand-authored
// Profiles (no SC2 file binds an arrow - verified against TheCore + Edennil).
const ARROW = /^Arrow(Up|Down|Left|Right)$/;

const FKEY = /^F([1-9]|1[0-2])$/;

/**
 * The ACTIVE keycodes a token lands on through a board layer. A board layer keys the
 * resolved layer by each key's ACTIVE (current-layer) keycode - what it emits in firmware - uniform
 * with a universal layout, where that code IS the KeyboardEvent.code. A code emitted by two physical
 * keys binds BOTH (twin-paint): the honest "every key that fires this command shows it" rule, no
 * dedup. The Ctrl-thumb and the real spacebar no longer collide because their active codes differ
 * (ControlLeft vs Space). Resolution targets the keys whose layer-active identity matches the token:
 *   - a mouse zone (or SC2 mouse token) stays its zone code - zones render on the mouse, not a key;
 *   - a named modifier matches every key whose active keycode is that family;
 *   - a positional token (Space, Tab, F-keys, NumPad, arrows) matches the key reporting that code;
 *   - a character token matches every key whose resolved char is that char.
 */
function tokenToBoardCodes(token: string, keyboard: Keyboard, layer: QmkBoardLayer): string[] {
  if (isZone(token)) return [token];
  const zone = MOUSE_TOKEN_TO_ZONE[token];
  if (zone) return [zone];
  // the same activeCodeOf rule compose keys caps by: the projection writes and the render reads
  // ONE code space, so the emit rule must not fork between them
  const activeCode = (i: number): string | null => activeCodeOf(keyboard, layer, i);
  // collect the ACTIVE codes of matching keys, deduped (two keys sharing one active code yield it
  // once; the binding then renders on every physical key carrying that code, via compose)
  const match = (pred: (code: string, i: number) => boolean): string[] => {
    const out = new Set<string>();
    for (let i = 0; i < keyboard.keys.length; i++) {
      const c = activeCode(i);
      if (c != null && pred(c, i)) out.add(c);
    }
    return [...out];
  };
  const fam = MODIFIER_TOKEN[token];
  if (fam) return match((c) => modifierFamily(c) === fam);
  const posCode =
    TOKEN_TO_CODE[token] ?? (FKEY.test(token) || ARROW.test(token) ? token : undefined);
  if (posCode) return match((c) => c === posCode);
  // a derived layer over a national layout (TheCore Plus) carries that layout's VK-token table, so it
  // resolves national OEM / digit tokens the same way the universal path does: the token
  // names a code directly (winning over its char reading), matched against the keys' identity codes.
  if (layer.tokens) {
    const viaTable = codeForLayoutToken(layer.tokens, token);
    if (viaTable) return match((c) => c === viaTable);
  }
  // an overflow-target token names the key the ISO Enter merge vacated, mirroring the universal
  // tokenToCodes rule (the derived layer carries the layout's overflow alongside its tokens)
  if (layer.overflow && layer.tokens) {
    for (const [contested, target] of Object.entries(layer.overflow)) {
      if (token !== target) continue;
      const vacated = new Set(
        Object.entries(layer.tokens)
          .filter(([, t]) => t === contested)
          .map(([code]) => code),
      );
      if (vacated.size > 0) return match((c) => vacated.has(c));
    }
  }
  const ch = tokenChar(token);
  if (ch != null) {
    const up = ch.toUpperCase();
    // exclude Numpad-coded keys, exactly as the universal tokenToCodes does: SC2 reserves NumPad tokens
    // for the numpad, so a character token never fires on one (else the same Profile paints an extra
    // wrong cap on a board lens that it never would on a universal layout).
    const codes = match(
      (c, i) => !c.startsWith("Numpad") && (layer.chars[i] ?? "").toUpperCase() === up,
    );
    if (codes.length > 0) return codes;
    // a digit with no char home (AZERTY's digit row emits symbols) falls back to its number-row key,
    // mirroring tokenToCodes - so Plus keeps its control groups on a national layout.
    if (layer.tokens && /^[0-9]$/.test(ch)) return match((c) => c === `Digit${ch}`);
  }
  return [];
}

/** A Binding's Command identity: the registry id where one exists, else the display name. The ONE
 * definition of "the same Command", shared by the collision rule (sameCommand) and the
 * alternate-yield grouping - a split definition would let the two disagree on what an Alternate is. */
function commandKey(b: Binding): string {
  return b.cmd != null ? `c:${b.cmd}` : `n:${b.name}`;
}

/** Two bindings fire the SAME Command (an Alternate, never a shadow). Used so a Command bound at
 * several keys does not shadow itself. */
function sameCommand(a: Binding, b: Binding): boolean {
  return commandKey(a) === commandKey(b);
}

/** A clone of `survivor` carrying `loser` (and anything `loser` itself shadowed) in `shadowed`,
 * flattened so entries never nest. Clones rather than mutating: a Binding is shared across the cells
 * a token twin-paints, so an in-place edit would leak the shadow onto every one. */
function withShadow(survivor: Binding, loser: Binding): Binding {
  const { shadowed: loserShadows, ...flatLoser } = loser;
  return {
    ...survivor,
    shadowed: [...(survivor.shadowed ?? []), flatLoser, ...(loserShadows ?? [])],
  };
}

/**
 * Place `incoming` on a cell, resolving any collision and recording the loser as a shadow on the
 * survivor so nothing is silently dropped (decision 0034). One rule, two callers:
 *   - within a tier (`incomingWins` false): FIRST placement wins - TheCore sets AllowSetConflicts=1,
 *     the earlier file/card entry is primary; the later one shadows.
 *   - across tiers (`incomingWins` true): the higher tier overrides the lower; the lower one shadows.
 * A filled default (source "default") never beats a real binding and yields silently (no shadow),
 * regardless of order or tier; two defaults never shadow each other. A shadow is recorded only when a
 * *different* Command is displaced - an Alternate of the same Command is not a collision.
 */
function resolveCell(layer: Layer, code: string, incoming: Binding, incomingWins: boolean): void {
  const cur = layer[code];
  if (!cur) {
    layer[code] = incoming;
    return;
  }
  const curDef = cur.source === "default";
  const incDef = incoming.source === "default";
  if (curDef !== incDef) {
    // a real binding always beats a filled default, silently
    layer[code] = curDef ? incoming : cur;
    return;
  }
  if (curDef) {
    // two defaults: pick by tier order, never shadow (game constants / filled-in gaps)
    if (incomingWins) layer[code] = incoming;
    return;
  }
  const winner = incomingWins ? incoming : cur;
  const loser = incomingWins ? cur : incoming;
  if (!sameCommand(winner, loser)) {
    layer[code] = withShadow(winner, loser);
    return;
  }
  // an Alternate replacement (the same Command re-placed): no new shadow, but the displaced
  // binding's own shadows must ride onto the survivor or they would be silently dropped
  const carried = loser.shadowed;
  layer[code] = carried?.length
    ? { ...winner, shadowed: [...(winner.shadowed ?? []), ...carried] }
    : winner;
}

/** One projected landing of a CharBind: the (combo, code) cell one alternate or twin paints. */
interface Placement {
  cb: CharBind;
  combo: string;
  code: string;
}

/**
 * The file alternates an alternate-yield game's engine never binds (decision 0042). The engine
 * resolves conflicts in the game's own key space, so a contest compares (combo, token) - never
 * projected codes: a code two DIFFERENT tokens share through the lens (the IntlBackslash twin,
 * decision 0039) is an artifact the engine never sees, and it keeps its 0034 shadow. A contested
 * alternate yields when its Command keeps another alternate that is uncontested and mappable on
 * this lens (the Command must stay visible); the whole alternate yields - its twin codes are one
 * key, never partially un-painted. Per contested key the yields run from the LAST-placed Command
 * backwards and stop before vacating it: when every contender could yield, the first-placed one
 * keeps the key, uniform with resolveCell's first-wins. A yield can free the key another Command
 * needs as its safe home, so the pass repeats until nothing moves (the drop set only grows, so it
 * terminates). Filled defaults neither force nor take part in a yield - resolveCell already
 * resolves them silently.
 */
function yieldedAlternates(placements: Placement[]): Set<CharBind> {
  interface Alt {
    cb: CharBind;
    cell: string;
    key: string;
  }
  // one entry per real, mappable alternate in the scope, in placement order (placements exist
  // only for tokens the lens projects, so an unmappable alternate neither contends nor licenses)
  const alts: Alt[] = [];
  const entered = new Set<CharBind>();
  for (const p of placements) {
    if (entered.has(p.cb) || p.cb.binding.source === "default") continue;
    entered.add(p.cb);
    alts.push({ cb: p.cb, cell: `${p.combo}|${p.cb.token}`, key: commandKey(p.cb.binding) });
  }
  const dropped = new Set<CharBind>();
  for (let changed = true; changed; ) {
    changed = false;
    // live command groups per key cell (both in first-placement order), and each command's
    // live alternates - the index that keeps the safe-home lookup linear
    const cells = new Map<string, Map<string, Alt[]>>();
    const byCommand = new Map<string, Alt[]>();
    for (const a of alts) {
      if (dropped.has(a.cb)) continue;
      const groups = cells.get(a.cell) ?? new Map<string, Alt[]>();
      const group = groups.get(a.key) ?? [];
      group.push(a);
      groups.set(a.key, group);
      cells.set(a.cell, groups);
      const own = byCommand.get(a.key) ?? [];
      own.push(a);
      byCommand.set(a.key, own);
    }
    const contested = (cell: string) => (cells.get(cell)?.size ?? 0) > 1;
    // a Command's safe home: another live alternate of its own on an uncontested key
    const safeHome = (a: Alt): boolean =>
      (byCommand.get(a.key) ?? []).some(
        (q) => !dropped.has(q.cb) && q.cell !== a.cell && !contested(q.cell),
      );
    for (const [cell, groups] of cells) {
      if (!contested(cell)) continue;
      let remaining = groups.size;
      for (const [, group] of [...groups].reverse()) {
        if (remaining < 2) break;
        if (!group.some(safeHome)) continue;
        for (const a of group) dropped.add(a.cb);
        remaining--;
        changed = true;
      }
    }
  }
  return dropped;
}

/**
 * The bindings of a SHARED tier that exist in this Context: one may be scoped to a subset
 * (`CharBind.contexts`, SC2's Protoss-only Warp In), and outside that subset it is not the
 * player's binding at all. Filtering here rather than at placement keeps a scoped binding out of
 * the collision resolution too, so its cell is free instead of shadowed.
 */
function scopedTo(binds: CharBind[], contextId: string): CharBind[] {
  return binds.some((cb) => cb.contexts)
    ? binds.filter((cb) => !cb.contexts || cb.contexts.has(contextId))
    : binds;
}

/** Project one tier's bindings into their placements, one per (combo, code) landing. */
function tierPlacements(binds: CharBind[], project: (token: string) => string[]): Placement[] {
  const placements: Placement[] = [];
  for (const cb of binds) {
    // project first: an unmappable token (an AZERTY accent viewed through QWERTY) must not
    // pre-create its combo's layer, or the modifier strip offers a dead latch identical to base
    const codes = project(cb.token);
    if (codes.length === 0) continue;
    const combo = normalizeCombo(cb.combo ?? "");
    for (const code of codes) placements.push({ cb, combo, code });
  }
  return placements;
}

/**
 * Build one tier's placements onto fresh layers, FIRST placement winning per (code, combo) -
 * TheCore sets AllowSetConflicts=1, so the earlier file/card entry is primary and a later
 * collision shadows it. A simple game's single tier rarely collides, so the rule choice is moot
 * for it. Alternates the yield pass dropped (decision 0042) never place.
 */
function buildTier(placements: Placement[], dropped?: Set<CharBind>): Layers {
  const layers: Layers = { base: {} };
  for (const p of placements) {
    if (dropped?.has(p.cb)) continue;
    let layer: Layer;
    if (p.combo) {
      layers.mods ??= {};
      layer = layers.mods[p.combo] ??= {};
    } else {
      layer = layers.base;
    }
    resolveCell(layer, p.code, p.cb.binding, false);
  }
  return layers;
}

/** Merge a higher tier over a lower one: its bindings override per code+combo (the higher tier wins,
 * except a filled default never displaces a real binding), recording the displaced lower binding as a
 * shadow on the survivor - see {@link resolveCell}. */
function mergeTier(target: Layers, src: Layers): void {
  for (const [code, binding] of Object.entries(src.base))
    resolveCell(target.base, code, binding, true);
  if (!src.mods) return;
  target.mods ??= {};
  for (const [combo, layer] of Object.entries(src.mods)) {
    const into = target.mods[combo] ?? {};
    target.mods[combo] = into;
    for (const [code, binding] of Object.entries(layer)) resolveCell(into, code, binding, true);
  }
}

/**
 * Project a character-space Profile through a lens into the positional resolved layer for one
 * Context - the single generic projector for every Game. The lens is the Keyboard plus
 * its active mapping (a universal Keyboard Layout or a board layer); the Context selects which
 * overlay tier applies. Positions are keyed by active code uniformly: a board layer
 * resolves a token to the active keycodes its keys emit; a universal layout to KeyboardEvent.codes.
 * A token landing on several keys binds them ALL (twin-paint, no dedup). The result feeds
 * resolveActiveLayer for the held modifier combo, exactly as the parsed paths did before.
 */
export function projectLayers(
  profile: Profile,
  contextId: string,
  keyboard: Keyboard,
  mapping: KeyboardLayout | BoardLayer,
): Layers {
  const charMap = charMapOf(keyboard, mapping);
  const boardLayer = keycodedBoardLayer(mapping);
  const project = (token: string): string[] =>
    boardLayer
      ? tokenToBoardCodes(token, keyboard, boardLayer)
      : projectToken(token, charMap, STANDARD_MODS);
  // The per-Context cluster underlay sits BELOW every shared base tier: it is placed first, so a real
  // flatten or card binding on a shared key overwrites it (card > flatten > cluster), while the
  // game-constant mouse defaults cannot clobber it (the source:"default" guard in resolveCell).
  const clusterTier = profile.contextBase?.[contextId];
  const overlay = profile.contexts?.[contextId];
  const underlay = clusterTier ? tierPlacements(clusterTier, project) : [];
  const shared = profile.base.map((tier) => tierPlacements(scopedTo(tier, contextId), project));
  const card = overlay ? tierPlacements(overlay, project) : [];
  // The alternate-yield's conflict scope (decision 0042) is the assembled in-game card: the
  // per-Context underlay plus the Context overlay, judged JOINTLY - the underlay's verbs are card
  // content (decision 0033). A shared base tier is a different in-game scope (a global fires
  // alongside a card command), so it never yields and a collision there keeps its 0034 shadow.
  const dropped =
    profile.alternateYield === true ? yieldedAlternates([...underlay, ...card]) : undefined;
  const layers: Layers = { base: {} };
  if (clusterTier) mergeTier(layers, buildTier(underlay, dropped));
  for (const tier of shared) mergeTier(layers, buildTier(tier));
  if (overlay) mergeTier(layers, buildTier(card, dropped));
  return layers;
}

/**
 * Drop every binding the Standard table filled in: keeps only what the player's own file
 * bound (and game constants stay hidden too, being source "default"). Backs the "show only my
 * bindings" toggle - the player either sees their whole effective setup or only what they changed.
 */
export function stripDefaults(layers: Layers): Layers {
  const keep = (layer: Layer): Layer => {
    const out: Layer = {};
    for (const [code, binding] of Object.entries(layer)) {
      if (binding.source !== "default") out[code] = binding;
    }
    return out;
  };
  const out: Layers = { base: keep(layers.base) };
  if (layers.mods) {
    out.mods = {};
    for (const [combo, layer] of Object.entries(layers.mods)) out.mods[combo] = keep(layer);
  }
  return out;
}
